/**
 * GameRuntime - game lifecycle management. Handles project building, bundle loading,
 * dependency injection, and game loop initialization for headless and browser modes.
 */

import path from 'path';

import { DEFAULT_GAME_BUNDLE_NAME, PROJECT_PATH_PREFIX } from '../const.js';
import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { AssetPath } from '../storage/storageProvider.js';
import { ClassRegistry } from '../systems/ClassRegistry.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { isNode } from '../utils/Utils.js';

import { createInjectedDependencies } from './InjectedDependencies.js';
import {
  findProjectFileForGame,
  getDefaultScene,
  getProjectEngineVersion,
  getStartupScene,
  loadProjectSettings
} from './Utils.js';

import type { BaseGameLoopOptions, IGameLoop } from '../game/GameLoop.js';
import type { NetSettings, NetWorld } from '../multiplayer/NetWorld.js';
import type { RendererType } from '../render/Renderer.js';
import type { IStorageProvider } from '../storage/storageProvider.js';


export interface GameRuntimeOptions {
  provider: IStorageProvider;
  /** Optional for headless mode */
  container?: HTMLElement;
  gameId: string;
  projectName?: string;
  buildProject?: boolean;
  skipClearGameClasses?: boolean;
  /** Run in headless server mode */
  headless?: boolean;
  /** Networking settings for multiplayer client/server runtime. */
  netSettings?: NetSettings;
  /** The default genesys engine module used to run the game */
  defaultEngineModule: any;
}

export class GameRuntime {
  protected gameLoop: IGameLoop | null = null;
  protected readonly isHeadless: boolean;

  constructor(protected readonly options: GameRuntimeOptions) {
    ObjectTracker.track(this);
    this.isHeadless = options.headless ?? (NetRuntime.isDedicatedServer() || isNode());
    // Validate options
    if (!this.isHeadless && !options.container) {
      throw new Error('container is required for non-headless mode');
    }
  }

  /**
   * Starts the game with an optional scene path.
   *
   * @param relativeScenePath - A scenePath relative to BUILT_PROJECT_FOLDER
   * @returns Success status and optional error message
   */
  public async startGame(relativeScenePath: string | null = null): Promise<{ success: boolean; error?: string }> {
    const provider = this.options.provider;
    if (this.options.buildProject) {
      console.time('build project');
      // Don't run tsc as it takes too much time
      if (!await provider.buildCurrentProject()) {
        return { success: false, error: 'Failed to build project' };
      }
      console.timeEnd('build project');
    }
    const projectFolder = PROJECT_PATH_PREFIX;
    const bundlePath = AssetPath.fromString(path.join(projectFolder, DEFAULT_GAME_BUNDLE_NAME));
    const projectFile = await findProjectFileForGame(this.options.provider) ?? '';
    const engineVersion = await getProjectEngineVersion(this.options.provider, projectFile);

    // Load project settings to get renderer configuration
    const [_, projectSettings] = await loadProjectSettings(this.options.provider, projectFile);
    const rendererType = projectSettings?.rendererType as RendererType | undefined;

    const findStartupScene = async () => {
      return await getStartupScene(this.options.provider, { forEditor: false, projectFilePath: projectFile });
    };

    const scenePath = relativeScenePath ?
      path.join(projectFolder, relativeScenePath) :
      await findStartupScene();

    if (scenePath) {
      console.log('[GameRuntime.startGame] running scenePath:', scenePath);
      if (!path.resolve(scenePath).startsWith(path.resolve(projectFolder))) {
        return { success: false, error: `Scene path must be within the built project folder: ${projectFolder}` };
      }
    } else {
      return { success: false, error: 'No scene path available' };
    }

    // Get default scene path for reload after disconnect (always use defaultScene from project)
    const defaultScenePath = await getDefaultScene(this.options.provider, projectFile);

    console.time('download bundle');
    const bundleText = await provider.downloadFileAsText(bundlePath);
    console.timeEnd('download bundle');
    console.time('resolve startup scene');
    const startupScenePath = AssetPath.fromString(scenePath);
    const defaultSceneAssetPath = defaultScenePath ? AssetPath.fromString(defaultScenePath) : undefined;
    console.timeEnd('resolve startup scene');

    const injectedDependencies = createInjectedDependencies(await this.resolveEngineModule(engineVersion));

    // simulate a node environment
    const module = { exports: {} };
    const exports = module.exports;

    // clear the game classes before running user code
    ClassRegistry.clearGameClasses();
    // Label evaluated bundle code so runtime stacks show a stable file name instead of VM frames.
    const bundleWithSourceUrl = `${bundleText}\n//# sourceURL=genesys-game-bundle.js`;
    const run = new Function('require', 'module', 'exports', bundleWithSourceUrl);
    run(injectedDependencies, module, exports);
    // module.exports contains all the exports from the code, which is exactly what we want
    const importedModule: any = module.exports;

    if (!importedModule) {
      console.error('Failed to import project bundle');
      return { success: false, error: 'Failed to import project bundle' };
    }

    if (!importedModule.main) {
      console.error('Failed to find main function in project bundle');
      return { success: false, error: 'Failed to find main function in project bundle' };
    }

    // Create a dummy container for headless mode
    const container = this.isHeadless ? document.createElement('div') : this.options.container!;

    // Prepare game loop options with renderer configuration from project settings
    const gameLoopOptions: Partial<BaseGameLoopOptions> = this.createGameLoopOptions(startupScenePath ?? null, defaultSceneAssetPath ?? null, rendererType ?? null);


    try {
      this.gameLoop = importedModule.main(container, gameLoopOptions);
    } catch (error) {
      console.error('[GameRuntime] Failed to create game loop:', error);
      const errorDetail = error instanceof Error ? (error.stack ?? error.message) : String(error);
      return { success: false, error: `Failed to create game loop: ${errorDetail}` };
    }
    if (!this.gameLoop) {
      console.error('Failed to create game loop');
      return { success: false, error: 'Failed to create game loop' };
    }

    console.log('[GameRuntime] Startup world path configured via GameContext');

    try {
      // Start the game loop first - this creates the world when using GameContext
      await this.gameLoop.start();
      console.log('[GameRuntime] Game loop started');

    } catch (error) {
      console.error('[GameRuntime] Failed to start game loop:', error);
      const errorDetail = error instanceof Error ? (error.stack ?? error.message) : String(error);
      return { success: false, error: `Failed to start game loop: ${errorDetail}` };
    }

    return { success: true };
  }

  public stopGame(): void {
    if (this.gameLoop) {
      this.gameLoop.destroy();
      this.gameLoop = null;
      if (!this.options.skipClearGameClasses) {
        ClassRegistry.clearGameClasses();
      }
      console.log('[GameRuntime] Game loop destroyed');
    }
  }

  public getGameLoop(): IGameLoop | null {
    return this.gameLoop;
  }

  public getNetWorld(): NetWorld | null {
    return this.gameLoop?.getWorld()?.netWorld ?? null;
  }

  protected async resolveEngineModule(engineVersion: string | null): Promise<any> {
    return this.options.defaultEngineModule;
  }

  protected createGameLoopOptions(
    startupScenePath: AssetPath | null,
    defaultSceneAssetPath: AssetPath | null,
    rendererType: RendererType | null
  ): Partial<BaseGameLoopOptions> {
    const gameLoopOptions: Partial<BaseGameLoopOptions> = {
      gameId: this.options.gameId,
      headless: this.isHeadless,
      gameContextConfig: {
        initialWorldPath: startupScenePath ?? undefined,
        defaultScenePath: defaultSceneAssetPath ?? undefined,
        netSettings: this.options.netSettings,
      }
    };

    if (rendererType) {
      gameLoopOptions.rendererOptions = { rendererType };
      console.log('[GameRuntime] using renderer type from project settings:', rendererType);
    }

    return gameLoopOptions;
  }
}
